import React, { ReactNode, useCallback } from 'react'
import { Controller, useForm, useFieldArray } from 'react-hook-form'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { addHours } from 'date-fns'
import dayjs from 'dayjs'
import { useMutation } from '@tanstack/react-query'
import { Button, ButtonVariant } from '../../../common/Button'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import { FormControl } from '../../../common/FormControl'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import {
  isUserAdminInSociety,
  isUserBoardMemberInSociety,
} from '../../../../helpers/society'
import { SelectDropdown } from '../../../common/SelectDropdown'
import { TextInput } from '../../../common/TextInput'
import {
  notificationSettingsOptions,
  NOTIFICATION_SETTINGS_VALUE,
} from '../../../../helpers/notification_settings_options'
import { societyEventCreateSchema } from '../../../../forms/schemas/society_event_create'
import { useAuthenticatedUserId } from '../../../../hooks/useAuthenticatedUserId'
import { TextArea } from '../../../common/TextArea'
import {
  dateTimeAsUTCWithoutCompensation,
  nearestHourDate,
  parsedOrNewDate,
} from '../../../../helpers/date'
import { DateTimeInput } from '../../../common/DateTimeInput'
import { Toggle } from '../../../common/Toggle'
import { IEventModel } from '../../../../interfaces/models/events.interfaces'
import { IBoardRoomEventModel } from '../../../../interfaces/models/boardroom-events.interfaces'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'
import { Label } from '../../../common/Label'
import { DropZone, DropZoneFile } from '../../../common/DropZone'
import { IDocumentModel } from '../../../../interfaces/models/documents.interfaces'
import { NDocuments } from '../../../../interfaces/services/documents.interfaces'
import { theme } from '../../../../theme/theme'
import { IconChoices } from '../../../common/Icon'
import { createDocument } from '../../../../api/documents'
import { DocumentView } from '../../../../generated/api/file-view'
import { i18nSocietyType } from '../../../../i18n'

interface CreateUpdateEventFormProps {
  onError: (error: unknown) => void
  onSubmit: (data: InferType<typeof societyEventCreateSchema>) => Promise<void>
  onClose: () => void
  loading: boolean
  event?: IEventModel | IBoardRoomEventModel
  boardRoomEvent?: boolean
  society: ISocietiesModel
  documents?: DocumentView[]
}

export const CreateUpdateEventForm = ({
  loading,
  onError,
  onSubmit,
  onClose,
  event,
  society,
  boardRoomEvent,
  documents,
}: CreateUpdateEventFormProps): ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { getErrorMessage } = useFormErrorMessage()
  const userId = useAuthenticatedUserId() as string

  const updateMode = !!event

  const isAdmin = isUserAdminInSociety(society, userId as string)
  const isBoard = isUserBoardMemberInSociety(society, userId as string)

  const canEdit = isAdmin || isBoard

  if ((!boardRoomEvent && !canEdit) || (updateMode && event === undefined)) {
    onClose()
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const getDefaultValues = useCallback((): InferType<
    typeof societyEventCreateSchema
  > => {
    return {
      title: updateMode ? event?.title || '' : '',
      location: updateMode ? event?.location || '' : '',
      description: updateMode ? event?.description || '' : '',
      isAllDay: updateMode ? event?.isAllDay || false : false,
      attendanceEnabled: updateMode ? event?.attendanceEnabled || false : true,
      startDate: updateMode
        ? parsedOrNewDate(event?.startDate)
        : addHours(nearestHourDate(), 2),
      endDate: updateMode
        ? parsedOrNewDate(event?.endDate)
        : addHours(nearestHourDate(), 3),
      notificationSettings: updateMode
        ? (event?.notificationSettings as NOTIFICATION_SETTINGS_VALUE) ||
          NOTIFICATION_SETTINGS_VALUE.NONE
        : NOTIFICATION_SETTINGS_VALUE.NOTIFICATION_AND_FEED,
      societyId: updateMode ? event?.societyId || society._id : society._id,
      userId: updateMode ? event?.userId || userId : userId,
      isCommentDisabled: event?.isCommentDisabled || false,
      documentsIds: updateMode
        ? documents?.map((document) => {
            return {
              id: document.id,
              name: document?.name,
            }
          }) || []
        : [],
    }
  }, [event, updateMode, userId, documents, society._id])

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(societyEventCreateSchema),
    defaultValues: getDefaultValues(),
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'documentsIds',
  })

  const { mutate: mutateCreateDocument, isPending: uploadingDocuments } =
    useMutation({
      mutationFn: createDocument,
      onSuccess: (data) => {
        const doc = data.data.data
        if (doc?._id) {
          append({ id: doc._id, name: doc.name || doc.filename })
        }
      },
    })

  const watchIsAllDay = watch('isAllDay')
  const watchStartDate = watch('startDate')
  const watchEndDate = watch('endDate')
  const watchSocietyId = watch('societyId')

  const handleDocumentUpload = async (file: DropZoneFile): Promise<void> => {
    const data = {
      data: file.base64,
      type: file.name.split('.').pop() as IDocumentModel['type'],
      filename: file.name,
      name: file.name,
      societyId: watchSocietyId,
      section: boardRoomEvent ? 'board-event' : 'event',
    } satisfies NDocuments.NCreate.IRequestBody

    mutateCreateDocument(data)
  }

  const minEventDate = dateTimeAsUTCWithoutCompensation(new Date())

  return (
    <>
      {!updateMode && (
        <Controller
          control={control}
          render={({ field: { onChange, value, name } }) => (
            <FormControl
              label={
                boardRoomEvent
                  ? translate('common.notificationSettings.labelBoard', {
                      context: i18nSocietyType(society?.societyTypeDisplayName),
                    })
                  : translate('common.notificationSettings.label', {
                      context: i18nSocietyType(society?.societyTypeDisplayName),
                    })
              }
              name={name}
              error={
                errors.notificationSettings &&
                getErrorMessage(errors.notificationSettings)
              }
            >
              <SelectDropdown
                options={notificationSettingsOptions}
                error={!!errors.notificationSettings}
                value={notificationSettingsOptions.find(
                  (option) => option.value === value
                )}
                onChange={(option) => onChange(option?.value)}
              />
            </FormControl>
          )}
          name="notificationSettings"
        />
      )}
      <Controller
        control={control}
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            label={translate('createUpdateEvent.form.labels.title')}
            error={errors.title && getErrorMessage(errors.title)}
            name={name}
          >
            <TextInput
              onChange={onChange}
              onBlur={onBlur}
              value={value}
              error={!!errors.title}
            />
          </FormControl>
        )}
        name="title"
      />
      {watch('notificationSettings') !== 'none' && (
        <Controller
          control={control}
          name="isCommentDisabled"
          render={({ field: { onChange, value, name } }) => (
            // eslint-disable-next-line max-len
            <div className="flex h-full w-full flex-col items-start justify-between space-y-3 md:w-1/2 md:flex-row md:items-center md:space-x-10 md:space-y-0">
              <Toggle
                name={name}
                enabled={!value}
                onChange={(e) => {
                  onChange(!e)
                }}
              />
              <Label
                name="postingDisabledLabel"
                label={translate('createUpdateEvent.form.labels.allowComments')}
              />
            </div>
          )}
        />
      )}
      <Controller
        control={control}
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            label={translate('createUpdateEvent.form.labels.location')}
            error={errors.location && getErrorMessage(errors.location)}
            name={name}
          >
            <TextInput
              onChange={onChange}
              onBlur={onBlur}
              value={value}
              error={!!errors.location}
            />
          </FormControl>
        )}
        name="location"
      />
      <Controller
        control={control}
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            label={translate('createUpdateEvent.form.labels.description')}
            error={errors.description && getErrorMessage(errors.description)}
            name={name}
          >
            <TextArea
              onChange={onChange}
              onBlur={onBlur}
              value={value}
              error={!!errors.description}
            />
          </FormControl>
        )}
        name="description"
      />
      <Controller
        control={control}
        render={({ field: { onChange, value, name } }) => (
          <Toggle
            label={translate('createUpdateEvent.form.labels.isAllDay')}
            name={name}
            enabled={value || false}
            onChange={onChange}
          />
        )}
        name="isAllDay"
      />
      <Controller
        control={control}
        render={({ field: { onChange, value, name } }) => (
          <FormControl
            label={
              watchIsAllDay
                ? translate('createUpdateEvent.form.labels.startDay')
                : translate('createUpdateEvent.form.labels.startDate')
            }
            error={errors.description && getErrorMessage(errors.description)}
            name={name}
          >
            <DateTimeInput
              onChange={(date) => {
                onChange(date)
                // startDate should always be before endDate. If the start
                // reaches or passes the end, move the end to exactly one hour
                // after the new start.
                if (watchEndDate && date >= watchEndDate) {
                  setValue('endDate', dayjs(date).add(1, 'hour').toDate())
                }
              }}
              minDate={new Date()}
              value={value}
              timeColumns={!watchIsAllDay}
            />
          </FormControl>
        )}
        name="startDate"
      />
      <Controller
        control={control}
        render={({ field: { onChange, value, name } }) => (
          <FormControl
            label={
              watchIsAllDay
                ? translate('createUpdateEvent.form.labels.endDay')
                : translate('createUpdateEvent.form.labels.endDate')
            }
            error={errors.description && getErrorMessage(errors.description)}
            name={name}
          >
            <DateTimeInput
              onChange={(date) => {
                onChange(date)
                // endDate should always be after startDate. If the end reaches
                // or passes the start, move the start to exactly one hour
                // before the new end.
                if (watchStartDate && date <= watchStartDate) {
                  const nextStartDate = dayjs(date).subtract(1, 'hour').toDate()
                  setValue(
                    'startDate',
                    nextStartDate < minEventDate ? minEventDate : nextStartDate
                  )
                }
              }}
              minDate={new Date()}
              value={value}
              timeColumns={!watchIsAllDay}
            />
          </FormControl>
        )}
        name="endDate"
      />
      <Controller
        control={control}
        render={({ field: { onChange, value, name } }) => (
          <Toggle
            label={translate('createUpdateEvent.form.labels.attendanceEnabled')}
            name={name}
            enabled={value || false}
            onChange={onChange}
          />
        )}
        name="attendanceEnabled"
      />
      <p style={theme.textVariants.base} className="mt-2">
        {translate('createUpdateContractForm.headers.documents')}
      </p>
      <Controller
        control={control}
        render={() => (
          <DropZone
            onUpload={(status, file) => {
              if (status === 'accepted') {
                handleDocumentUpload(file)
              } else {
                setToastNotification(
                  ToastType.DANGER,
                  translate('flashMessage.somethingWentWrongError')
                )
              }
            }}
            multiple
          />
        )}
        name="documentsIds"
      />
      <div className="flex flex-col gap-2">
        {fields.map((field, index) => (
          <div
            key={field.name}
            className="flex w-full flex-row items-center space-x-4"
          >
            <Controller
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <FormControl className="w-full" name={`files.${index}.name`}>
                  <div className="flex w-full flex-row gap-4">
                    <TextInput
                      wrapperClassName="w-full"
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      disabled
                      error={!!errors.documentsIds?.[index]?.name}
                    />
                    <Button
                      className="h-12 w-12"
                      icon={IconChoices.DELETE_TRASH}
                      variant={ButtonVariant.DANGER}
                      onClick={() => remove(index)}
                    />
                  </div>
                </FormControl>
              )}
              name={`documentsIds.${index}.name`}
            />
          </div>
        ))}
      </div>
      <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-3">
        <Button
          label={translate('common.actions.cancel')}
          onClick={onClose}
          variant={ButtonVariant.TEXT}
        />
        <Button
          variant={ButtonVariant.PRIMARY}
          disabled={!isValid || loading}
          loading={uploadingDocuments}
          label={translate('common.actions.save')}
          type="submit"
          onClick={handleSubmit(onSubmit, onError)}
        />
      </div>
    </>
  )
}
